import React from "react";
import {
  Award,
  Camera,
  Home,
  MessageSquare,
  Search,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { AppLink } from "../ui/link";
import { cn } from "../../lib/utils";
import type { RouteMap } from "../../pages/MainAppPage/types";
import { mobileScrollLinkClass } from "./navLinkStyles";
import { AdminRouteMap } from "@/pages/AdminAppPage/types";

type DashboardNavVariant = "sidebar" | "mobileMenu";

type DashboardNavLinksProps = {
  routes: RouteMap;
  adminRoutes?: AdminRouteMap;
  admin?: boolean;
  canAccessBackend?: boolean;
  currentPath: string;
  unreadQuoteCount?: number;
  incomingOrgInvitationCount?: number;
  hasWebsite?: boolean;
  hasJobSitePhotos?: boolean;
  hasCredentials?: boolean;
  variant: DashboardNavVariant;
  onNavigate?: () => void;
};

type NavSection = "main" | "website" | "company" | "account";

type DashboardNavItem = {
  id:
    | "home"
    | "jobs"
    | "credentials"
    | "quotes"
    | "features"
    | "team"
    | "settings"
    | "admin";
  label: string;
  href: NavHref;
  icon: LucideIcon;
  section: NavSection;
  mobileOnly?: boolean;
  requiresWebsite?: boolean;
  requiresJobSitePhotos?: boolean;
  requiresCredentials?: boolean;
  teammateVisible?: boolean;
  requiresRoute?: keyof RouteMap;
  isActive: (currentPath: string, routes: RouteMap) => boolean;
};

const homeHref = (
  routes: RouteMap,
  hasWebsite: boolean,
  canAccessBackend: boolean,
) => {
  if (!canAccessBackend) return routes.teammateHome ?? "/teammate";
  return hasWebsite ? routes.dashboard : routes.onboarding;
};

const settingsHref = (routes: RouteMap) =>
  routes.settings ?? "/dashboard/settings";

const membersHref = (routes: RouteMap) =>
  routes.organizationMembers ?? "/organization/members";

type NavHref = (
  routes: RouteMap,
  hasWebsite: boolean,
  canAccessBackend: boolean,
) => string;

const DASHBOARD_NAV_ITEMS: DashboardNavItem[] = [
  {
    id: "home",
    label: "Home",
    href: homeHref,
    icon: Home,
    section: "main",
    teammateVisible: true,
    isActive: (currentPath, routes) =>
      currentPath === routes.dashboard ||
      currentPath === `${routes.dashboard}/` ||
      currentPath === routes.onboarding ||
      currentPath.startsWith(`${routes.onboarding}/`) ||
      currentPath === (routes.teammateHome ?? "/teammate"),
  },
  {
    id: "jobs",
    label: "Jobs",
    href: (routes) => routes.dashboardJobs ?? "/dashboard/jobs",
    icon: Camera,
    section: "main",
    requiresJobSitePhotos: true,
    teammateVisible: true,
    isActive: (currentPath, routes) => {
      const path = routes.dashboardJobs ?? "/dashboard/jobs";
      return currentPath === path || currentPath.startsWith(`${path}/`);
    },
  },
  {
    id: "credentials",
    label: "Credentials",
    href: (routes) => routes.dashboardCredentials ?? "/dashboard/credentials",
    icon: Award,
    section: "main",
    requiresCredentials: true,
    teammateVisible: true,
    isActive: (currentPath, routes) => {
      const path = routes.dashboardCredentials ?? "/dashboard/credentials";
      return currentPath === path || currentPath.startsWith(`${path}/`);
    },
  },
  {
    id: "team",
    label: "Team",
    href: membersHref,
    icon: Users,
    section: "company",
    requiresRoute: "organizationMembers",
    isActive: (currentPath, routes) => {
      const members = membersHref(routes);
      return currentPath === members || currentPath.startsWith(`${members}/`);
    },
  },
  {
    id: "quotes",
    label: "Quotes",
    href: (routes) => routes.quotes,
    icon: MessageSquare,
    section: "website",
    requiresWebsite: true,
    isActive: (currentPath, routes) =>
      currentPath === routes.quotes ||
      currentPath.startsWith(`${routes.quotes}/`),
  },
  {
    id: "features",
    label: "Site",
    href: (routes) => routes.features,
    icon: Search,
    section: "website",
    requiresWebsite: true,
    isActive: (currentPath, routes) =>
      currentPath === routes.features ||
      currentPath.startsWith(`${routes.features}/`),
  },
  {
    id: "settings",
    label: "Settings",
    href: settingsHref,
    icon: Settings,
    section: "company",
    teammateVisible: true,
    isActive: (currentPath, routes) => {
      const settings = settingsHref(routes);
      return (
        currentPath === settings ||
        currentPath.startsWith(`${settings}/`) ||
        currentPath === routes.account ||
        currentPath.startsWith(`${routes.account}/`) ||
        currentPath === routes.billing ||
        currentPath.startsWith(`${routes.billing}/`)
      );
    },
  },
  {
    id: "admin",
    label: "Admin",
    href: () => "",
    icon: ShieldCheck,
    section: "account",
    mobileOnly: true,
    isActive: (currentPath, routes) =>
      currentPath === routes.admin ||
      currentPath.startsWith(`${routes.admin}/`),
  },
];

const SECTION_LABELS: Record<NavSection, string> = {
  main: "Main",
  website: "Website",
  company: "Company",
  account: "Account",
};

const sidebarLinkClass = (isActive: boolean) =>
  cn(
    "no-underline hover:no-underline flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all border",
    isActive
      ? "bg-premium-50 text-premium-600 shadow-sm border-premium-100/50 hover:text-premium-600"
      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground border-transparent",
  );

const mobileLinkClass = (isActive: boolean) =>
  cn(
    mobileScrollLinkClass,
    "flex items-center gap-3",
    isActive && "bg-premium-50/70 text-premium-700",
  );

const sectionHeadingClass =
  "text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3 px-3 whitespace-nowrap overflow-hidden";

export const DashboardNavLinks: React.FC<DashboardNavLinksProps> = ({
  routes,
  adminRoutes,
  admin = false,
  canAccessBackend = true,
  currentPath,
  unreadQuoteCount = 0,
  incomingOrgInvitationCount = 0,
  hasWebsite = false,
  hasJobSitePhotos = false,
  hasCredentials = false,
  variant,
  onNavigate,
}) => {
  const visible = (item: DashboardNavItem) => {
    if (!canAccessBackend && !item.teammateVisible) return false;
    if (item.requiresWebsite && !hasWebsite) return false;
    if (item.requiresJobSitePhotos && !hasJobSitePhotos) return false;
    if (item.requiresCredentials && !hasCredentials) return false;
    if (item.requiresRoute && !routes[item.requiresRoute]) return false;
    if (item.mobileOnly && variant === "sidebar") return false;
    if (item.id === "admin" && (!admin || !adminRoutes)) return false;
    return true;
  };

  const itemsFor = (section: NavSection) =>
    DASHBOARD_NAV_ITEMS.filter(
      (item) => item.section === section && visible(item),
    );

  const linkClass = variant === "sidebar" ? sidebarLinkClass : mobileLinkClass;
  const mainItems = itemsFor("main");
  const websiteItems = itemsFor("website");
  const companyItems = itemsFor("company");
  const accountItems = itemsFor("account");

  const renderItems = (items: DashboardNavItem[]) =>
    items.map((item) => {
      const routesForActive =
        item.id === "admin" && adminRoutes
          ? (adminRoutes as unknown as RouteMap)
          : routes;
      const isActive = item.isActive(currentPath, routesForActive);
      const Icon = item.icon;
      const href =
        item.id === "admin"
          ? (adminRoutes as AdminRouteMap).admin
          : item.href(routes, hasWebsite, canAccessBackend);
      return (
        <AppLink
          key={item.id}
          to={href}
          variant="muted"
          className={linkClass(isActive)}
          onClick={onNavigate}
        >
          <Icon className="w-4 h-4 shrink-0" />
          <span className="whitespace-nowrap overflow-hidden">
            {item.label}
          </span>
          {item.id === "quotes" && unreadQuoteCount > 0 && (
            <span className="ml-auto bg-premium-100 text-premium-600 py-0.5 px-2 rounded-full text-[10px] font-bold whitespace-nowrap">
              {unreadQuoteCount}
            </span>
          )}
          {item.id === "team" && incomingOrgInvitationCount > 0 && (
            <span className="ml-auto bg-premium-100 text-premium-600 py-0.5 px-2 rounded-full text-[10px] font-bold whitespace-nowrap">
              {incomingOrgInvitationCount}
            </span>
          )}
        </AppLink>
      );
    });

  const sidebarSection = (
    section: NavSection,
    items: DashboardNavItem[],
    extraClass = "",
  ) => {
    if (items.length === 0) return null;
    return (
      <React.Fragment key={section}>
        {variant === "sidebar" ? (
          <div className={cn(sectionHeadingClass, extraClass)}>
            {SECTION_LABELS[section]}
          </div>
        ) : null}
        {renderItems(items)}
      </React.Fragment>
    );
  };

  return (
    <>
      {sidebarSection("main", mainItems)}
      {sidebarSection("website", websiteItems, "mt-8")}
      {sidebarSection("company", companyItems, "mt-8")}
      {variant === "mobileMenu" && accountItems.length > 0 ? (
        <div className="h-px w-full bg-border my-1.5" />
      ) : null}
      {renderItems(accountItems)}
    </>
  );
};
